import React, { useContext, useEffect, useMemo, useState } from "react";
import HeroSection from "./HeroSection";
import FeaturesSection from "./FeaturesSection";
import Reassurance from "./Reassurance";
import GroupedProducts from "./GroupedProducts";
import Testimonials from "./Testimonials";
import CTASection from "./CTASection";
import { ThemeContext } from "../../hooks/ThemeContext";
import { api } from "../../services/baseUrl";
import { formatDate, formatPercent } from "../../utils/formatters";

const Home = () => {
  const { theme } = useContext(ThemeContext);
  const [homeStats, setHomeStats] = useState({ totalProducts: 0, recentRatings: [], activePromo: null, activePromos: [] });
  const isDark = theme === "dark";

  useEffect(() => {
    let isMounted = true;

    api
      .get("/home/stats")
      .then((response) => {
        if (!isMounted) return;
        const payload = response?.data || {};
        setHomeStats({
          totalProducts: payload.total_products || 0,
          recentRatings: payload.recent_ratings || [],
          activePromo: payload.active_promo || null,
          activePromos: payload.active_promos || [],
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setHomeStats((prev) => prev);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Mélange aléatoire et sélection des 5 derniers avis, même sans commentaire
  const ratingMessages = useMemo(() => {
    if (!homeStats.recentRatings?.length) return [];
    // Mélanger aléatoirement
    const shuffled = [...homeStats.recentRatings].sort(() => Math.random() - 0.5);
    // Prendre les 5 premiers
    return shuffled.slice(0, 5).map((rating) => {
      const name = rating.user_name || "Client";
      const handle = name
        .toLowerCase()
        .replace(/\s+/g, "")
        .slice(0, 16);
      return {
        author: name,
        handle: handle || "client",
        text: rating.comment && rating.comment.length > 1 ? rating.comment : `a noté ${rating.product_name || "une création"}`,
        rating: rating.rating,
        productName: rating.product_name || "Création",
        created_at: rating.created_at,
      };
    });
  }, [homeStats.recentRatings]);

  const promoList = homeStats.activePromos?.length
    ? homeStats.activePromos
    : homeStats.activePromo
      ? [homeStats.activePromo]
      : [];

  const hasMultiplePromos = promoList.length > 1;
  const marqueePromos = promoList;
  const promoDuration = Math.max(12, promoList.length * 8);

  return (
    <div
      className={`relative flex-1 flex flex-col overflow-hidden ${isDark ? "bg-[#09111c] text-white" : "bg-[#fcf8f6] text-gray-800"}`}
    >
      <div className={`pointer-events-none absolute inset-0 ${isDark ? 'bg-[radial-gradient(circle_at_top,rgba(244,63,94,0.16),transparent_30%),radial-gradient(circle_at_80%_18%,rgba(168,85,247,0.12),transparent_24%)]' : 'bg-[radial-gradient(circle_at_top,rgba(244,63,94,0.12),transparent_62%)]'}`} />
      <div className={`pointer-events-none absolute right-[-120px] top-24 h-72 w-72 rounded-full blur-3xl ${isDark ? 'bg-rose-500/18' : 'bg-rose-200/60'}`} />
      <div className={`pointer-events-none absolute left-[-140px] top-[38rem] h-80 w-80 rounded-full blur-3xl ${isDark ? 'bg-purple-500/12' : 'bg-white/90'}`} />
      <div className="w-full max-w-7xl mx-auto px-4">
        <HeroSection totalCreations={homeStats.totalProducts} />
      </div>

      {promoList.length > 0 && (
        <div className="w-full max-w-7xl mx-auto px-4 mt-8">
          <div
            className={`relative overflow-hidden border ${
              isDark ? "border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl" : "border-rose-100/80 bg-gradient-to-br from-rose-50 via-white to-orange-50 shadow-[0_24px_70px_rgba(15,23,42,0.08)]"
            }`}
          >
            {hasMultiplePromos && (
              <style>{`
                @keyframes promoMarquee {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(calc(-100% * (var(--promo-count) - 1))); }
                }
                .promo-marquee { animation: promoMarquee var(--promo-duration) linear infinite; }
                .promo-marquee:hover { animation-play-state: paused; }
              `}</style>
            )}
            <div className={`absolute -right-10 -top-10 h-32 w-32 rounded-full ${isDark ? 'bg-rose-500/20' : 'bg-rose-200/90'}`} />
            <div className={`absolute -left-12 -bottom-12 h-40 w-40 rounded-full ${isDark ? 'bg-purple-500/15' : 'bg-purple-100/80'}`} />
            <div className="relative px-6 py-5 md:px-8 md:py-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-[0.25em] ${isDark ? "text-rose-200" : "text-rose-600"}`}>
                      Promo active
                    </p>
                    <h3 className={`text-2xl md:text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                      {hasMultiplePromos ? 'Promos en cours' : (
                        promoList[0]?.product_name
                          ? `Sur ${promoList[0].product_name}`
                          : 'Promo speciale Krayne Agency'
                      )}
                    </h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`px-4 py-2 rounded-full text-sm font-semibold ${isDark ? "bg-white/10 text-white/90 border border-white/10" : "bg-white text-rose-700 border border-rose-100"}`}>
                      Non cumulable
                    </div>
                  </div>
                </div>

                {!hasMultiplePromos && (
                  <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Remise de {formatPercent(promoList[0].discount, 0)}
                    {promoList[0].end_date ? ` • Valable jusqu'au ${formatDate(promoList[0].end_date)}` : ''}
                  </p>
                )}

                {hasMultiplePromos && (
                  <div className="relative">
                    <div className="overflow-hidden rounded-[32px] p-1 ">
                      <div
                        className="promo-marquee flex items-stretch gap-6 "
                        style={{ width: "max-content", "--promo-count": marqueePromos.length, "--promo-duration": `${promoDuration}s` }}
                      >
                        {marqueePromos.map((promo, index) => (
                          <div
                            key={`${promo.id_product ?? promo.id}-${index}`}
                            className={`min-w-[280px] max-w-xs border px-6 py-4 text-sm shadow-xl bg-white/5 ${
                              isDark ? 'border-white/10 bg-white/[0.06] text-white/85 shadow-[0_10px_30px_rgba(0,0,0,0.18)] backdrop-blur-sm' : 'border-rose-100 bg-white/95 text-gray-700'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-semibold">
                                {promo.product_name || 'Produit Krayne'}
                              </div>
                              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${isDark ? 'bg-rose-500/15 text-rose-200 border border-rose-400/20' : 'bg-rose-50 text-rose-600'}`}>
                                -{formatPercent(promo.discount, 0)}
                              </div>
                            </div>
                            {promo.end_date && (
                              <div className={`mt-2 text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                Valable jusqu'au {formatDate(promo.end_date)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-7xl mx-auto px-4 mt-10">
        <FeaturesSection />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 mt-8">
        <Reassurance />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 mt-12">
        <Testimonials messages={ratingMessages} />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 mt-8">
        <GroupedProducts />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 mt-6">
        <CTASection />
      </div>
    </div>
  );
};

export default Home;




